import { useState, useEffect, useCallback, useMemo } from "react";
import { API } from "../../../cache";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "../../../AppSidebar";
import { FilterTriggerButton } from "../../common/components";
import SavedFiltersPanel from "../../common/components/SavedFiltersPanel";
import { listFilterTemplates } from "../../common/services/filterTemplatesApi";
import TimesheetEntryDrawer from "../components/TimesheetEntryDrawer";
import TimesheetEditDrawer from "../components/TimesheetEditDrawer";
import "../../../DailyTimesheetView.css";
import "../../../V2SideDrawer.css";
import "../../../MasterViewV2.css";
import "../styles/TimesheetViewV2Page.css";

const STATUS_OPTS   = ["Draft", "Submitted", "Approved"];
const REF_TYPE_OPTS = ["project", "task", "feature", "sub_feature", "dependency", "prerequisite", "defect", "meeting"];
const REF_TYPE_LABELS = {
  project:      "Projects",
  task:         "Tasks",
  feature:      "Module",
  sub_feature:  "Sub Module",
  dependency:   "Dependency",
  prerequisite: "Pre-requisite",
  defect:       "Defect",
  meeting:      "Meeting",
};

const REF_TYPE_COLORS = {
  project:      { bg: "#e8eaf6", color: "#1a237e" },
  task:         { bg: "#e8f5e9", color: "#2e7d32" },
  feature:      { bg: "#e3f2fd", color: "#1565c0" },
  sub_feature:  { bg: "#f3e5f5", color: "#6a1b9a" },
  dependency:   { bg: "#fff8e1", color: "#f57f17" },
  prerequisite: { bg: "#fce4ec", color: "#c62828" },
  defect:       { bg: "#ffebee", color: "#b71c1c" },
  meeting:      { bg: "#e0f2f1", color: "#00695c" },
};

const STATUS_STYLE = {
  Draft:     { bg: "#fff8e1", color: "#f57f17" },
  Submitted: { bg: "#e3f2fd", color: "#1565c0" },
  Approved:  { bg: "#e8f5e9", color: "#2e7d32" },
};

const TIMESHEET_FILTER_FIELDS = [
  {
    key: "date_range",
    label: "Date Range",
    type: "daterange",
  },
  {
    key: "status",
    label: "Status",
    type: "choice",
    options: STATUS_OPTS.map(s => ({ value: s, label: s })),
  },
  {
    key: "ref_type",
    label: "Type",
    type: "choice",
    options: REF_TYPE_OPTS.map(t => ({ value: t, label: REF_TYPE_LABELS[t] })),
  },
];

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtTime(h, m) {
  const hrs  = h || 0;
  const mins = m || 0;
  if (hrs === 0 && mins === 0) return "—";
  if (hrs === 0)  return `${mins}m`;
  if (mins === 0) return `${hrs}h`;
  return `${hrs}h ${mins}m`;
}

function currentMonthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function currentMonthEnd() {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
}

// ── Timesheet Filter Popover ───────────────────────────────────────────────────
function TimesheetFilterPopover({ open, appliedFilters, filterMode, onApply, onClose, onApplyTemplate }) {
  const [draft,    setDraft]    = useState({});
  const [mode,     setMode]     = useState("all");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDraft(JSON.parse(JSON.stringify(appliedFilters || {})));
    setMode(filterMode || "all");
    setExpanded(null);
  }, [open, appliedFilters, filterMode]);

  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const setOp = (key, op) =>
    setDraft(d => ({ ...d, [key]: { op, values: d[key]?.values || [] } }));

  const toggleVal = (key, val) =>
    setDraft(d => {
      const curr = d[key]?.values || [];
      const next = curr.includes(val) ? curr.filter(v => v !== val) : [...curr, val];
      return { ...d, [key]: { op: d[key]?.op || "is", values: next } };
    });

  const clearField = (key) =>
    setDraft(d => {
      const next = { ...d };
      delete next[key];
      return next;
    });

  const handleReset = () => {
    setDraft({});
    setMode("all");
  };

  const handleApply = () => {
    onApply(draft, mode);
    onClose();
  };

  return (
    <>
      <div className="mv2-fdr-scrim" onClick={onClose} />
      <div className="mv2-fdr">
        <div className="mv2-fdr-header">
          <span className="mv2-fdr-title">Filter</span>
          <button className="mv2-fdr-reset" onClick={handleReset}>Reset</button>
        </div>

        <div className="mv2-fdr-saved-wrap">
          <SavedFiltersPanel
            module="timesheet"
            appliedFilters={draft}
            filterMode={mode}
            onApply={(filterDef, fm) => {
              setDraft(JSON.parse(JSON.stringify(filterDef)));
              setMode(fm || "all");
              onApplyTemplate?.(filterDef, fm || "all");
              onApply(filterDef, fm || "all");
              onClose();
            }}
          />
        </div>

        <div className="mv2-fdr-list">
          {TIMESHEET_FILTER_FIELDS.map(field => {
            const isOpen = expanded === field.key;
            const curr = field.type === "daterange"
              ? (draft[field.key] || { from: "", to: "" })
              : (draft[field.key] || { op: "is", values: [] });
            const hasActive = field.type === "daterange"
              ? !!(curr.from || curr.to)
              : curr.values?.length > 0;

            return (
              <div
                key={field.key}
                className={`mv2-fdr-field${isOpen ? " mv2-fdr-field--open" : ""}${hasActive ? " mv2-fdr-field--active" : ""}`}
              >
                <button
                  className="mv2-fdr-field-hdr"
                  onClick={() => setExpanded(isOpen ? null : field.key)}
                >
                  <span className="mv2-fdr-field-name">
                    {field.label}
                    {hasActive && (
                      <span className="mv2-fdr-badge">
                        {field.type === "daterange" ? "✓" : curr.values.length}
                      </span>
                    )}
                  </span>
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    {isOpen
                      ? <polyline points="18 15 12 9 6 15" />
                      : <polyline points="9 18 15 12 9 6" />
                    }
                  </svg>
                </button>

                {isOpen && (
                  <div className="mv2-fdr-field-body">
                    {field.type === "daterange" ? (
                      <div className="mv2-fdr-daterange">
                        <div className="mv2-fdr-daterange-row">
                          <label className="mv2-fdr-daterange-label">From</label>
                          <input
                            type="date"
                            className="mv2-fdr-date-input"
                            value={curr.from || ""}
                            max={curr.to || undefined}
                            onChange={e => setDraft(d => ({ ...d, [field.key]: { type: "daterange", ...d[field.key], from: e.target.value } }))}
                          />
                        </div>
                        <div className="mv2-fdr-daterange-row">
                          <label className="mv2-fdr-daterange-label">To</label>
                          <input
                            type="date"
                            className="mv2-fdr-date-input"
                            value={curr.to || ""}
                            min={curr.from || undefined}
                            onChange={e => setDraft(d => ({ ...d, [field.key]: { type: "daterange", ...d[field.key], to: e.target.value } }))}
                          />
                        </div>
                        {(curr.from || curr.to) && (
                          <button
                            className="mv2-fdr-daterange-clear"
                            onClick={() => setDraft(d => ({ ...d, [field.key]: { type: "daterange", from: "", to: "" } }))}
                          >
                            Clear dates
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="mv2-fdr-op-row">
                          <select
                            className="mv2-fdr-op"
                            value={curr.op}
                            onChange={e => setOp(field.key, e.target.value)}
                          >
                            <option value="is">Is</option>
                            <option value="is_not">Is Not</option>
                          </select>
                          {hasActive && (
                            <button
                              type="button"
                              className="mv2-fdr-field-reset"
                              onClick={() => clearField(field.key)}
                            >
                              Reset this filter
                            </button>
                          )}
                        </div>
                        <div className="mv2-fdr-opts">
                          {/* Alphabetical with currently-selected values
                              pinned to the top — recomputed live off
                              curr.values so checking a box moves it up
                              immediately. */}
                          {field.options.slice().sort((a, b) => {
                            const aSel = curr.values?.includes(a.value);
                            const bSel = curr.values?.includes(b.value);
                            if (aSel !== bSel) return aSel ? -1 : 1;
                            return a.label.localeCompare(b.label);
                          }).map(opt => (
                            <label key={opt.value} className="mv2-fdr-opt">
                              <input
                                type="checkbox"
                                checked={curr.values.includes(opt.value)}
                                onChange={() => toggleVal(field.key, opt.value)}
                              />
                              <span>{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mv2-fdr-mode">
          <label className="mv2-fdr-mode-opt">
            <input
              type="radio"
              name="tsFdrMode"
              value="any"
              checked={mode === "any"}
              onChange={() => setMode("any")}
            />
            Any of these
          </label>
          <label className="mv2-fdr-mode-opt">
            <input
              type="radio"
              name="tsFdrMode"
              value="all"
              checked={mode === "all"}
              onChange={() => setMode("all")}
            />
            All of these
          </label>
        </div>

        <div className="mv2-fdr-footer">
          <button className="mv2-fdr-find" onClick={handleApply}>Find</button>
          <button className="mv2-fdr-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function TimesheetViewV2Page({ showToast, user }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sb-collapsed") === "true"
  );

  const [reports,       setReports]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    date_range: { type: "daterange", from: currentMonthStart(), to: currentMonthEnd() },
  }));
  const [filterMode, setFilterMode] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    listFilterTemplates("timesheet").then((templates) => {
      const def = (templates || []).find((t) => t.is_default);
      if (def) { setAppliedFilters(def.filter_def || {}); setFilterMode(def.filter_mode || "all"); }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filterDateFrom = appliedFilters.date_range?.from || "";
  const filterDateTo   = appliedFilters.date_range?.to   || "";

  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [editReportId, setEditReportId] = useState(null);

  const [entities,        setEntities]        = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(true);


  const isAdmin = ["Admin", "Super Admin"].includes(user?.db_role);
  const [adminStats,        setAdminStats]        = useState(null);
  const [adminStatsLoading, setAdminStatsLoading] = useState(false);

  /* ── Load ref entities ──
     Reference picker is restricted to Task / Defect / Dependency only (no
     Project / Feature / Sub-Feature), scoped to work actually associated
     with the current user.
     NOTE: this previously scoped via project_user_mapping (GET /users/{id}/projects),
     which turned out to be the wrong signal — that table is a separate,
     admin-curated access-grant list (populated only through the admin
     "Edit User" UI, used for external/customer-level access control) and is
     NOT populated by task/defect assignment. In production ~14-19% of users
     with actively-assigned tasks have zero project_user_mapping rows, so
     that approach silently returned nothing for them. Use view=my UNION
     view=team instead (team_module_mapping / team_user_mapping — the same
     mechanism /tasks and /defects already use for their own scoping), which
     per this app's convention is enforced for every caller regardless of
     admin role. view=my alone would be enough for direct assignment, but
     view=team's scope requires the user to also be a team_user_mapping
     member of the task's assigned team — not logically guaranteed just
     because they're the assignee — so both are fetched and merged to be
     certain a user's own directly-assigned work always appears. */
  const loadEntities = useCallback(async () => {
    setEntitiesLoading(true);
    try {
      const mergeById = (a, b) => {
        const itemsA = Array.isArray(a) ? a : (a.items || []);
        const itemsB = Array.isArray(b) ? b : (b.items || []);
        const byId = new Map();
        [...itemsA, ...itemsB].forEach(item => { if (item.id) byId.set(item.id, item); });
        return [...byId.values()];
      };

      const [myTasks, teamTasks, dr, myDefects, teamDefects] = await Promise.all([
        fetch(`${API}/tasks?view=my&page_size=2000`, { credentials: "include" }).then(r => r.json()).catch(() => ({ items: [] })),
        fetch(`${API}/tasks?view=team&page_size=2000`, { credentials: "include" }).then(r => r.json()).catch(() => ({ items: [] })),
        // No team/project filter param exists on /dependency-requests, so
        // scope client-side to dependencies I requested or that were
        // requested of me — a direct, reliable per-user signal.
        fetch(`${API}/dependency-requests`, { credentials: "include" }).then(r => r.json()).catch(() => []),
        fetch(`${API}/defects?view=my&limit=1000`, { credentials: "include" }).then(r => r.json()).catch(() => ({ items: [] })),
        fetch(`${API}/defects?view=team&limit=1000`, { credentials: "include" }).then(r => r.json()).catch(() => ({ items: [] })),
      ]);

      const unified = [];

      mergeById(myTasks, teamTasks).forEach(f => {
        if (f.id) {
          unified.push({
            id: f.id, ref_type: "task",
            label: [f.task_code, f.feature].filter(Boolean).join(" — ") || "—",
            sublabel: [f.customer, f.project].filter(Boolean).join(" / "),
            sub_feature: f.sub_feature || "",
            description: f.description || "",
          });
        }
      });
      if (Array.isArray(dr)) {
        dr.forEach(d => {
          if (d.id && (d.requested_by === user?.db_user_id || d.requested_to === user?.db_user_id)) {
            unified.push({
              id: d.id, ref_type: "dependency",
              label: d.title || "Untitled dependency",
              sublabel: [d.dependency_type, d.status].filter(Boolean).join(" · "),
              project_name: d.project_name || d.customer_name || null,
            });
          }
        });
      }
      mergeById(myDefects, teamDefects).forEach(d => {
        if (d.id) {
          unified.push({
            id: d.id, ref_type: "defect",
            label: [d.defect_code, d.title].filter(Boolean).join(" — "),
            sublabel: [d.project_name, d.status].filter(Boolean).join(" · "),
            project_name: d.project_name || null,
          });
        }
      });

      setEntities(unified);
    } catch { /* autocomplete degrades gracefully */ }
    setEntitiesLoading(false);
  }, [user]);

  useEffect(() => { loadEntities(); }, [loadEntities]);

  /* ── Load reports (date range server-side; status/type client-side) ── */
  const loadReports = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterDateFrom)   params.set("date_from", filterDateFrom);
    if (filterDateTo)     params.set("date_to",   filterDateTo);
    if (user?.db_user_id) params.set("user_id",   user.db_user_id);
    try {
      const r = await fetch(`${API}/daily-reports?${params}`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setReports(Array.isArray(data) ? data : []);
    } catch {
      showToast("Failed to load reports", "warn");
    }
    setLoading(false);
  }, [filterDateFrom, filterDateTo, user?.db_user_id, showToast]);

  useEffect(() => { loadReports(); }, [loadReports]);

  /* ── Admin consolidated stats ── */
  const loadAdminStats = useCallback(async () => {
    if (!isAdmin) return;
    setAdminStatsLoading(true);
    const params = new URLSearchParams();
    if (filterDateFrom) params.set("date_from", filterDateFrom);
    if (filterDateTo)   params.set("date_to",   filterDateTo);
    params.set("limit", "2000");
    try {
      const r = await fetch(`${API}/daily-reports?${params}`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const data = await r.json();
      let rows = Array.isArray(data) ? data : [];

      const sf = appliedFilters.status;
      if (sf?.values?.length > 0) {
        rows = sf.op === "is_not"
          ? rows.filter(rpt => !sf.values.includes(rpt.status))
          : rows.filter(rpt => sf.values.includes(rpt.status));
      }
      const rf = appliedFilters.ref_type;
      if (rf?.values?.length > 0) {
        rows = rf.op === "is_not"
          ? rows.filter(rpt => !rf.values.includes(rpt.ref_type))
          : rows.filter(rpt => rf.values.includes(rpt.ref_type));
      }

      let totalMins = 0, taskMins = 0, defectMins = 0;
      rows.forEach(rpt => {
        const mins = (rpt.hours_spent || 0) * 60 + (rpt.minutes_spent || 0);
        totalMins += mins;
        if (rpt.ref_type === "task")   taskMins   += mins;
        if (rpt.ref_type === "defect") defectMins += mins;
      });
      const toHM = m => ({ hours: Math.floor(m / 60), minutes: m % 60 });
      setAdminStats({ total: toHM(totalMins), tasks: toHM(taskMins), defects: toHM(defectMins) });
    } catch {
      setAdminStats(null);
    }
    setAdminStatsLoading(false);
  }, [isAdmin, filterDateFrom, filterDateTo, appliedFilters]);

  useEffect(() => { loadAdminStats(); }, [loadAdminStats]);

  /* ── Submit draft report (row action) ── */
  const submitReport = async (id, e) => {
    e.stopPropagation();
    try {
      const r = await fetch(`${API}/daily-reports/${id}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Submitted" }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || "Failed to submit report");
      // Backend decides the real final status (Approved if within the
      // epic's budget, Submitted otherwise) — don't assume "Submitted".
      const finalStatus = data?.status || "Submitted";
      showToast(finalStatus === "Approved" ? "Report submitted and auto-approved" : "Report submitted");
      if (data?.budget_warning) window.alert(data.budget_warning);
      setReports(prev => prev.map(rpt => rpt.id === id ? { ...rpt, status: finalStatus } : rpt));
    } catch (e) {
      showToast(e?.message || "Failed to submit report", "warn");
    }
  };

  /* ── Delete report ── */
  const deleteReport = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this daily report?")) return;
    try {
      const r = await fetch(`${API}/daily-reports/${id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error();
      showToast("Report deleted");
      setReports(prev => prev.filter(rpt => rpt.id !== id));
      if (editReportId === id) setEditReportId(null);
    } catch {
      showToast("Failed to delete report", "warn");
    }
  };

  const TYPE_DISPLAY = { task: "task" };

  const getProjectLabel = useCallback((rpt) => {
    if (rpt.resolved_project_name) return rpt.resolved_project_name;
    const { ref_id: refId, ref_type: refType } = rpt;
    if (!refId) return null;
    const entity = entities.find(e => e.id === refId && e.ref_type === refType);
    if (!entity) return null;
    if (refType === "project") return entity.label;
    if (refType === "task") {
      const parts = (entity.sublabel || "").split(" / ");
      return parts.length > 1 ? parts[parts.length - 1] : entity.sublabel || null;
    }
    if (refType === "dependency") return entity.project_name || null;
    if (refType === "defect")     return entity.project_name || null;
    return null;
  }, [entities]);

  /* ── Client-side status/type filter ── */
  const filteredReports = useMemo(() => {
    let result = reports;
    const sf = appliedFilters.status;
    if (sf?.values?.length > 0) {
      result = sf.op === "is_not"
        ? result.filter(rpt => !sf.values.includes(rpt.status))
        : result.filter(rpt => sf.values.includes(rpt.status));
    }
    const rf = appliedFilters.ref_type;
    if (rf?.values?.length > 0) {
      result = rf.op === "is_not"
        ? result.filter(rpt => !rf.values.includes(rpt.ref_type))
        : result.filter(rpt => rf.values.includes(rpt.ref_type));
    }
    return result;
  }, [reports, appliedFilters]);

  const timeSummary = useMemo(() => {
    let totalMins = 0;
    const byType = {};
    filteredReports.forEach(rpt => {
      const mins = (rpt.hours_spent || 0) * 60 + (rpt.minutes_spent || 0);
      totalMins += mins;
      if (rpt.ref_type) byType[rpt.ref_type] = (byType[rpt.ref_type] || 0) + mins;
    });
    const toHM = (m) => ({ hours: Math.floor(m / 60), minutes: m % 60 });
    return {
      total: toHM(totalMins),
      byType: Object.entries(byType)
        .sort((a, b) => b[1] - a[1])
        .map(([type, mins]) => ({ type, ...toHM(mins) })),
    };
  }, [filteredReports]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    const dr = appliedFilters.date_range;
    if (dr?.from || dr?.to) count++;
    if (appliedFilters.status?.values?.length > 0) count++;
    if (appliedFilters.ref_type?.values?.length > 0) count++;
    return count;
  }, [appliedFilters]);

  const [activeView, setActiveView] = useState("summary");

  const summaryRows = useMemo(() => {
    const byDate = {};
    filteredReports.forEach(rpt => {
      const dt = rpt.report_date || "unknown";
      if (!byDate[dt]) byDate[dt] = { date: dt, taskMins: 0, defectMins: 0, meetingMins: 0, totalMins: 0 };
      const mins = (rpt.hours_spent || 0) * 60 + (rpt.minutes_spent || 0);
      byDate[dt].totalMins += mins;
      if (rpt.ref_type === "task")    byDate[dt].taskMins    += mins;
      if (rpt.ref_type === "defect")  byDate[dt].defectMins  += mins;
      if (rpt.ref_type === "meeting") byDate[dt].meetingMins += mins;
    });
    return Object.values(byDate).sort((a, b) => b.date.localeCompare(a.date));
  }, [filteredReports]);


  /* ── Render ── */
  return (
    <div
      className="dts2-root"
      style={{ gridTemplateColumns: sidebarCollapsed ? `${SB_W_COLLAPSED} 1fr` : `${SB_W} 1fr` }}
    >
      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed(c => {
          const next = !c;
          localStorage.setItem("sb-collapsed", String(next));
          return next;
        })}
      />

      <div className="dts2-shell">
        {/* ── Header ── */}
        <header className="page-head">
          <h1>
            Timesheet{" "}
            <em>{loading ? "loading…" : `${filteredReports.length} entries`}</em>
          </h1>
          <div className="page-head-actions">
            <button className="v2-btn-primary" onClick={() => setDrawerOpen(true)}>
              + New Entry
            </button>
          </div>
        </header>

        {/* ── Toolbar ── */}
        <div className="page-toolbar">
          <div className="page-tabs">
            <button
              className={`page-tab${activeView === "summary" ? " page-tab--active" : ""}`}
              onClick={() => setActiveView("summary")}
            >
              Summary View
            </button>
            <button
              className={`page-tab${activeView === "detail" ? " page-tab--active" : ""}`}
              onClick={() => setActiveView("detail")}
            >
              Detailed View
            </button>
          </div>
          <div className="page-toolbar-right">
            <div className="dfv2-filter-wrap">
              <FilterTriggerButton
                activeCount={activeFilterCount}
                onClick={() => setFilterOpen(o => !o)}
                onClear={() => {
                  setAppliedFilters({});
                  setFilterMode("all");
                }}
              />
              <TimesheetFilterPopover
                open={filterOpen}
                appliedFilters={appliedFilters}
                filterMode={filterMode}
                onApply={(draft, mode) => { setAppliedFilters(draft); setFilterMode(mode); }}
                onApplyTemplate={(filters, mode) => { setAppliedFilters(filters); setFilterMode(mode); }}
                onClose={() => setFilterOpen(false)}
              />
            </div>
            <span className="toolbar__count">
              {filteredReports.length} report{filteredReports.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="dts2-body">

          {/* Admin consolidated cards */}
          {isAdmin && (
            <div className="dts-admin-stats">
              <div className="dts-admin-stats__label">All Users — Consolidated</div>
              <div className="dts-admin-stats__cards">
                <div className="dts-admin-card dts-admin-card--total">
                  <div className="dts-admin-card__title">Total Time Spent</div>
                  <div className="dts-admin-card__value">
                    {adminStatsLoading ? "…" : adminStats ? fmtTime(adminStats.total.hours, adminStats.total.minutes) : "—"}
                  </div>
                </div>
                <div className="dts-admin-card dts-admin-card--tasks">
                  <div className="dts-admin-card__title">Total Time on Tasks</div>
                  <div className="dts-admin-card__value">
                    {adminStatsLoading ? "…" : adminStats ? fmtTime(adminStats.tasks.hours, adminStats.tasks.minutes) : "—"}
                  </div>
                </div>
                <div className="dts-admin-card dts-admin-card--defects">
                  <div className="dts-admin-card__title">Total Time on Defects</div>
                  <div className="dts-admin-card__value">
                    {adminStatsLoading ? "…" : adminStats ? fmtTime(adminStats.defects.hours, adminStats.defects.minutes) : "—"}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Time summary by type */}
          {!loading && filteredReports.length > 0 && (
            <div className="status-overview">
              <div className="status-overview__title">Your Time Log</div>
              <div className="status-overview__grid">
                <div className="status-overview__item">
                  <div className="status-overview__badge">Total Time</div>
                  <div className="status-overview__count">{fmtTime(timeSummary.total.hours, timeSummary.total.minutes)}</div>
                </div>
                {timeSummary.byType.map(({ type, hours, minutes }) => (
                  <div key={type} className="status-overview__item">
                    <div className="status-overview__badge">{REF_TYPE_LABELS[type] || type}</div>
                    <div className="status-overview__count">{fmtTime(hours, minutes)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary View */}
          {activeView === "summary" && (
            <div className="dts2-list-area">
              <div className="page-table-wrap">
                <table className="page-table" style={{ tableLayout: "fixed", width: "100%" }}>
                  <colgroup>
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="num">Task Hrs</th>
                      <th className="num">Defect Hrs</th>
                      <th className="num">Meeting Hrs</th>
                      <th className="num">Total Hrs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={5} className="dts-state">Loading…</td></tr>
                    )}
                    {!loading && summaryRows.length === 0 && (
                      <tr><td colSpan={5} className="dts-state">No reports found. Create one to get started.</td></tr>
                    )}
                    {!loading && summaryRows.map(row => (
                      <tr key={row.date}>
                        <td>{fmtDate(row.date)}</td>
                        <td className="num">
                          <span className="dts-time-val">
                            {row.taskMins > 0 ? fmtTime(Math.floor(row.taskMins / 60), row.taskMins % 60) : "—"}
                          </span>
                        </td>
                        <td className="num">
                          <span className="dts-time-val">
                            {row.defectMins > 0 ? fmtTime(Math.floor(row.defectMins / 60), row.defectMins % 60) : "—"}
                          </span>
                        </td>
                        <td className="num">
                          <span className="dts-time-val">
                            {row.meetingMins > 0 ? fmtTime(Math.floor(row.meetingMins / 60), row.meetingMins % 60) : "—"}
                          </span>
                        </td>
                        <td className="num">
                          <span className="dts-time-val">
                            {fmtTime(Math.floor(row.totalMins / 60), row.totalMins % 60)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Detailed View */}
          {activeView === "detail" && (
            <div className="dts2-list-area">
              <div className="page-table-wrap">
                <table className="page-table">
                  <colgroup>
                    <col className="c-date" />
                    <col className="c-title" />
                    <col className="c-type" />
                    <col className="c-project" />
                    <col className="c-user" />
                    <col className="c-time" />
                    <col className="c-status" />
                    <col className="c-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Title</th>
                      <th>Type</th>
                      <th>Project</th>
                      <th>Submitted By</th>
                      <th className="num">Time</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={8} className="dts-state">Loading…</td></tr>
                    )}
                    {!loading && filteredReports.length === 0 && (
                      <tr><td colSpan={8} className="dts-state">No reports found. Create one to get started.</td></tr>
                    )}
                    {!loading && filteredReports.map(rpt => (
                      <tr
                        key={rpt.id}
                        className={editReportId === rpt.id ? "dts-row--selected" : ""}
                        onClick={() => setEditReportId(rpt.id)}
                      >
                        <td>{fmtDate(rpt.report_date)}</td>
                        <td className="dts-td--title-cell">
                          {rpt.title || <span className="dts-empty">Untitled</span>}
                        </td>
                        <td>
                          {rpt.ref_type
                            ? <span className="dts-type-tag">{TYPE_DISPLAY[rpt.ref_type] || rpt.ref_type}</span>
                            : <span className="dts-empty">—</span>}
                        </td>
                        <td>{getProjectLabel(rpt) || <span className="dts-empty">—</span>}</td>
                        <td>{rpt.user_display_name || rpt.user_name || <span className="dts-empty">—</span>}</td>
                        <td className="num">
                          <span className="dts-time-val">{fmtTime(rpt.hours_spent, rpt.minutes_spent)}</span>
                        </td>
                        <td>
                          <span
                            className="dts-badge"
                            style={{ background: STATUS_STYLE[rpt.status]?.bg, color: STATUS_STYLE[rpt.status]?.color }}
                          >
                            {rpt.status}
                          </span>
                        </td>
                        <td>
                          <div className="page-row-actions">
                            {rpt.status === "Draft" && (
                              <button className="dts-submit-btn" onClick={e => submitReport(rpt.id, e)} title="Submit report">Submit</button>
                            )}
                            <button className="dts-del-btn" onClick={e => deleteReport(rpt.id, e)} title="Delete report">✕</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>{/* /dts2-body */}

        <TimesheetEditDrawer
          open={!!editReportId}
          reportId={editReportId}
          entities={entities}
          entitiesLoading={entitiesLoading}
          showToast={showToast}
          onClose={() => setEditReportId(null)}
          onSaved={(updated) => {
            setReports(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
          }}
        />

        <TimesheetEntryDrawer
          open={drawerOpen}
          entities={entities}
          entitiesLoading={entitiesLoading}
          showToast={showToast}
          onClose={() => setDrawerOpen(false)}
          onSaved={async () => {
            await loadReports();
          }}
        />

      </div>{/* /dts2-shell */}
    </div>
  );
}
